import { DatacentralHookForm } from '@/components/ui/form-field/datacentral-hook-form'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    createServiceVatRate,
    updateServiceVatRate,
} from '@/modules/Service/services'
import type { ServiceVatRate } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'vnetwork-ui'

export interface ServiceVatRateModalProps extends ModalProps {
    serviceID: number
    vatRate?: ServiceVatRate
}

interface FormValues {
    tax_rate: number
    effective_from: string
    effective_to: string | null
    description: string
}

const ServiceVatRateModal: React.FC<ServiceVatRateModalProps> = ({
    open,
    onClose,
    serviceID,
    vatRate,
}) => {
    const { t } = useTranslation('services')
    const { t: tCommon } = useTranslation()
    const isEdit = !!vatRate

    const formControl = useForm<FormValues>({
        defaultValues: {
            tax_rate: vatRate?.tax_rate ?? 0.1,
            effective_from: vatRate?.effective_from
                ? dayjs(vatRate.effective_from).format('YYYY-MM-DD')
                : dayjs().format('YYYY-MM-DD'),
            effective_to: vatRate?.effective_to
                ? dayjs(vatRate.effective_to).format('YYYY-MM-DD')
                : null,
            description: vatRate?.description || '',
        },
    })

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES_VAT_RATES],
    })

    const { mutateAsync: createMutation, isPending: isCreating } =
        useMutationWithHandler(
            { mutationFn: createServiceVatRate },
            {
                successMessage: t('vat.create_success', {
                    defaultValue: 'Tạo thuế VAT thành công',
                }),
                reactQueryKeys: QUERY_KEYS.SERVICES_VAT_RATES,
                onSuccess: () => {
                    onClose()
                    refetch()
                },
            }
        )

    const { mutateAsync: updateMutation, isPending: isUpdating } =
        useMutationWithHandler(
            { mutationFn: updateServiceVatRate },
            {
                successMessage: t('vat.update_success', {
                    defaultValue: 'Cập nhật thuế VAT thành công',
                }),
                reactQueryKeys: QUERY_KEYS.SERVICES_VAT_RATES,
                onSuccess: () => {
                    onClose()
                    refetch()
                },
            }
        )

    const isPending = isCreating || isUpdating

    const onSubmit = (values: FormValues) => {
        const data = {
            tax_rate: Number(values.tax_rate),
            effective_from: values.effective_from,
            effective_to: values.effective_to || undefined,
            description: values.description || undefined,
        }

        if (isEdit && vatRate) {
            updateMutation({ serviceID, vatRateID: vatRate.id, data })
        } else {
            createMutation({ serviceID, data })
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                isEdit
                    ? t('vat.edit', { defaultValue: 'Chỉnh sửa thuế VAT' })
                    : t('vat.add', { defaultValue: 'Thêm thuế VAT' })
            }
        >
            <DatacentralHookForm<FormValues>
                formControl={formControl}
                onSubmit={onSubmit}
                columns={2}
                data={[
                    {
                        formItemProps: {
                            name: 'tax_rate',
                            label: t('vat.tax_rate'),
                            type: 'number',
                            required: true,
                        },
                        colSpan: 2,
                    },
                    {
                        formItemProps: {
                            name: 'effective_from',
                            label: t('vat.effective_from'),
                            type: 'datepicker',
                            required: true,
                        },
                        colSpan: 1,
                    },
                    {
                        formItemProps: {
                            name: 'effective_to',
                            label: t('vat.effective_to'),
                            type: 'datepicker',
                        },
                        colSpan: 1,
                    },
                    {
                        formItemProps: {
                            name: 'description',
                            label: t('vat.description'),
                            type: 'textarea',
                        },
                        colSpan: 2,
                    },
                ]}
                actions={[
                    {
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        title: tCommon('cancel'),
                    },
                    {
                        type: 'submit',
                        variant: 'default',
                        title: tCommon('save'),
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
            />
        </Dialog>
    )
}

export default ServiceVatRateModal
